import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Divider,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  OpenInFull as ExpandIcon,
  LocationOn,
  AccessTime,
  Business,
  LocalHospital,
  Assessment,
  People,
  AttachMoney,
  Streetview,
  Close as CloseIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Placeholder data - will be replaced with Supabase data
const sampleVolumeData = [
  { month: 'Jan', value: 150, trend: 145 },
  { month: 'Feb', value: 180, trend: 160 },
  { month: 'Mar', value: 200, trend: 175 },
  { month: 'Apr', value: 220, trend: 190 },
  { month: 'May', value: 190, trend: 205 },
  { month: 'Jun', value: 240, trend: 220 },
];

const sampleRevenueData = [
  { month: 'Jan', value: 450000, trend: 440000 },
  { month: 'Feb', value: 480000, trend: 460000 },
  { month: 'Mar', value: 520000, trend: 480000 },
  { month: 'Apr', value: 490000, trend: 500000 },
  { month: 'May', value: 550000, trend: 520000 },
  { month: 'Jun', value: 580000, trend: 540000 },
];

// Sample provider data
const sampleProviderData = [
  { name: 'Dr. Smith', specialty: 'Primary Care', schedule: 'Mon-Fri 9-5', productivity: 95 },
  { name: 'Dr. Johnson', specialty: 'Cardiology', schedule: 'Mon-Thu 8-6', productivity: 88 },
  { name: 'Dr. Williams', specialty: 'Orthopedics', schedule: 'Tue-Fri 8-4', productivity: 92 },
];

interface FacilityDashboardProps {
  facility: {
    id: number;
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    beds?: number;
  };
  onClose: () => void;
}

interface SectionHeaderProps {
  title: string;
  expandable?: boolean;
  onExpand?: () => void;
}

interface ExpandedViewProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const ExpandedView: React.FC<ExpandedViewProps> = ({ open, onClose, title, children }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
  >
    <DialogTitle>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {title}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
    <DialogContent>
      {children}
    </DialogContent>
  </Dialog>
);

const FacilityDashboard: React.FC<FacilityDashboardProps> = ({ facility, onClose }) => {
  const [volumeTimeRange, setVolumeTimeRange] = useState('6m');
  const [revenueTimeRange, setRevenueTimeRange] = useState('6m');
  const [customDateRange, setCustomDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const SectionHeader: React.FC<SectionHeaderProps> = ({ title, expandable = false, onExpand }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Typography variant="h6">{title}</Typography>
      {expandable && (
        <IconButton size="small" onClick={onExpand}>
          <ExpandIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  const handleExpandSection = (section: string) => {
    setExpandedSection(section);
  };

  return (
    <Paper
      sx={{
        width: 400,
        height: '100%',
        position: 'absolute',
        right: 0,
        top: 0,
        zIndex: 1000,
        overflowY: 'auto',
        p: 2,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <SectionHeader title="Facility Information" />
        <Typography variant="subtitle2" gutterBottom>Location ID</Typography>
        <Typography variant="body1" gutterBottom>{facility.id}</Typography>
        <Typography variant="subtitle2" gutterBottom>Common Name</Typography>
        <Typography variant="h5" gutterBottom>{facility.name}</Typography>
        <Typography variant="subtitle2" gutterBottom>Address</Typography>
        <Typography variant="body1">
          {facility.address}<br />
          {facility.city}, {facility.state} {facility.zip}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 3 }}>
        <SectionHeader title="Operations" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Hours of Operation</Typography>
            <Typography>Mon-Fri: 8AM-5PM</Typography>
            <Typography>Sat-Sun: Closed</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Lease Expiration</Typography>
            <Typography>Dec 31, 2025</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Square Footage</Typography>
            <Typography>4,350 SF</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Rent per SF</Typography>
            <Typography>$30/SF</Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 3 }}>
        <SectionHeader 
          title="Services & Staff" 
          expandable 
          onExpand={() => handleExpandSection('services')}
        />
        <Typography variant="subtitle2" gutterBottom>Service Lines & Specialties</Typography>
        <Box sx={{ mb: 2 }}>
          <Chip label="Primary Care" size="small" sx={{ mr: 1, mb: 1 }} />
          <Chip label="Cardiology" size="small" sx={{ mr: 1, mb: 1 }} />
          <Chip label="Orthopedics" size="small" sx={{ mr: 1, mb: 1 }} />
        </Box>
        <Typography variant="subtitle2" gutterBottom>Subspecialties</Typography>
        <Box sx={{ mb: 2 }}>
          <Chip label="Sports Medicine" size="small" sx={{ mr: 1, mb: 1 }} />
          <Chip label="Interventional Cardiology" size="small" sx={{ mr: 1, mb: 1 }} />
        </Box>
        <Typography variant="subtitle2" gutterBottom>Provider FTEs</Typography>
        <Grid container spacing={1}>
          <Grid item xs={8}>Primary Care</Grid>
          <Grid item xs={4}>3.0</Grid>
          <Grid item xs={8}>Cardiology</Grid>
          <Grid item xs={4}>2.0</Grid>
          <Grid item xs={8}>Orthopedics</Grid>
          <Grid item xs={4}>1.5</Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 3 }}>
        <SectionHeader 
          title="Facility Details" 
          expandable
          onExpand={() => handleExpandSection('details')}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Exam Rooms</Typography>
            <Typography>12</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">X-Ray Rooms</Typography>
            <Typography>1</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Procedure Rooms</Typography>
            <Typography>2</Typography>
          </Grid>
          {facility.beds && (
            <Grid item xs={6}>
              <Typography variant="subtitle2">Beds</Typography>
              <Typography>{facility.beds}</Typography>
            </Grid>
          )}
          <Grid item xs={6}>
            <Typography variant="subtitle2">Iris Care Score</Typography>
            <Typography>4.2/5.0</Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 3 }}>
        <SectionHeader 
          title="Patient Volume" 
          expandable
          onExpand={() => handleExpandSection('volume')}
        />
        <Box sx={{ mb: 1 }}>
          <Select
            size="small"
            value={volumeTimeRange}
            onChange={(e) => setVolumeTimeRange(e.target.value as string)}
            fullWidth
          >
            <MenuItem value="1m">Last Month</MenuItem>
            <MenuItem value="6m">Last 6 Months</MenuItem>
            <MenuItem value="ytd">YTD</MenuItem>
            <MenuItem value="1y">1 Year</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </Box>
        {volumeTimeRange === 'custom' && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <DatePicker
                label="Start Date"
                value={customDateRange[0]}
                onChange={(date: Date | null) => setCustomDateRange([date, customDateRange[1]])}
              />
              <DatePicker
                label="End Date"
                value={customDateRange[1]}
                onChange={(date: Date | null) => setCustomDateRange([customDateRange[0], date])}
              />
            </Box>
          </LocalizationProvider>
        )}
        <Box sx={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sampleVolumeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#8884d8" />
              <Line type="monotone" dataKey="trend" stroke="#82ca9d" strokeDasharray="3 3" />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 3 }}>
        <SectionHeader 
          title="Patient Service Revenue" 
          expandable
          onExpand={() => handleExpandSection('revenue')}
        />
        <Box sx={{ mb: 1 }}>
          <Select
            size="small"
            value={revenueTimeRange}
            onChange={(e) => setRevenueTimeRange(e.target.value as string)}
            fullWidth
          >
            <MenuItem value="1m">Last Month</MenuItem>
            <MenuItem value="6m">Last 6 Months</MenuItem>
            <MenuItem value="ytd">YTD</MenuItem>
            <MenuItem value="1y">1 Year</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </Box>
        {revenueTimeRange === 'custom' && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <DatePicker
                label="Start Date"
                value={customDateRange[0]}
                onChange={(date: Date | null) => setCustomDateRange([date, customDateRange[1]])}
              />
              <DatePicker
                label="End Date"
                value={customDateRange[1]}
                onChange={(date: Date | null) => setCustomDateRange([customDateRange[0], date])}
              />
            </Box>
          </LocalizationProvider>
        )}
        <Box sx={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sampleRevenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#8884d8" />
              <Line type="monotone" dataKey="trend" stroke="#82ca9d" strokeDasharray="3 3" />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 3 }}>
        <SectionHeader 
          title="Street View" 
          expandable
          onExpand={() => handleExpandSection('streetview')}
        />
        <Box
          sx={{
            height: 200,
            bgcolor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => handleExpandSection('streetview')}
        >
          <Typography color="text.secondary">Click to view street view</Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary">
          Data Last Refreshed: {new Date().toLocaleString()}
        </Typography>
      </Box>

      {/* Expanded Views */}
      <ExpandedView
        open={expandedSection === 'services'}
        onClose={() => setExpandedSection(null)}
        title="Services & Staff Details"
      >
        <Box>
          <Typography variant="h6" gutterBottom>Provider Details</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Specialty</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Productivity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sampleProviderData.map((provider) => (
                <TableRow key={provider.name}>
                  <TableCell>{provider.name}</TableCell>
                  <TableCell>{provider.specialty}</TableCell>
                  <TableCell>{provider.schedule}</TableCell>
                  <TableCell>{provider.productivity}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </ExpandedView>

      <ExpandedView
        open={expandedSection === 'details'}
        onClose={() => setExpandedSection(null)}
        title="Facility Details"
      >
        <Box>
          <Typography variant="h6" gutterBottom>Iris Care Score Breakdown</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Patient Satisfaction</Typography>
              <Typography>4.5/5.0</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Clinical Outcomes</Typography>
              <Typography>4.3/5.0</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Staff Engagement</Typography>
              <Typography>4.0/5.0</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Facility Quality</Typography>
              <Typography>4.1/5.0</Typography>
            </Grid>
          </Grid>
        </Box>
      </ExpandedView>

      <ExpandedView
        open={expandedSection === 'volume'}
        onClose={() => setExpandedSection(null)}
        title="Patient Volume Details"
      >
        <Box>
          <Typography variant="h6" gutterBottom>Volume by Service Line</Typography>
          {/* Add detailed volume breakdown */}
        </Box>
      </ExpandedView>

      <ExpandedView
        open={expandedSection === 'revenue'}
        onClose={() => setExpandedSection(null)}
        title="Revenue Details"
      >
        <Box>
          <Typography variant="h6" gutterBottom>Revenue Breakdown</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">By Service Line</Typography>
              {/* Add service line revenue breakdown */}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">By Provider</Typography>
              {/* Add provider revenue breakdown */}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">By Payor Type</Typography>
              {/* Add payor type revenue breakdown */}
            </Grid>
          </Grid>
        </Box>
      </ExpandedView>

      <ExpandedView
        open={expandedSection === 'streetview'}
        onClose={() => setExpandedSection(null)}
        title="Street View"
      >
        <Box sx={{ height: 500, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography>Street View Integration Placeholder</Typography>
        </Box>
      </ExpandedView>
    </Paper>
  );
};

export default FacilityDashboard; 